'use client';
import React, { useMemo, useRef, useState } from 'react';
import { BoxProps, Checkbox } from '@mui/material';
import { FileUpload as FileUploadIcon, UploadFile } from '@mui/icons-material';

import { BodyMedium, Center, Column, Row } from '@dh/styles';
import { useBreakpoint, useDragAndDrop, useTheme, useToggle, useUser } from '@dh/hooks';
import {
    FileToUpload,
    FileUploadError,
    getBreakpointValue,
    isCompact,
    MixedMedia
} from '@dh/types';
import { FileType, isImage, isLink, isUploadedMedia, isURL, onEnter } from '@dh/utils';
import { validateFile } from '@dh/utils/files';

import {
    SourceInputs,
    UploadBox,
    UploadBoxSmall,
    UploadInfoText,
    UploadInfoTextSmall
} from './styles';
import { DHTextField, IconButton, ModalDialog, PMPSnackbar, TextButton } from '..';
import { FileList } from './FileList';
import { Aspect, CropModal } from './CropModal';
import { PrivacyHint } from './PrivacyHint';
import { useTranslations } from 'next-intl';

interface Props extends BoxProps {
    title?: string;
    infoText?: string;
    uploads?: MixedMedia[];
    updateUploads: (files: MixedMedia[]) => void;
    updateHasError: (hasError: boolean) => void;
    currentFilesTotalSize?: number;
    allowedFileTypes?: FileType[];
    withDescription?: boolean;
    withSource?: boolean;
    maxFileCount?: number;
    withLinkUpload?: boolean;
    withCropping?: boolean;
    withPrivacyHint?: boolean;
    small?: boolean;
    fixedAspectRatio?: Aspect | number;
}

export function FileUpload({
    currentFilesTotalSize = 0,
    title,
    infoText,
    uploads = [],
    updateUploads,
    updateHasError,
    withDescription,
    withSource,
    maxFileCount,
    withLinkUpload,
    allowedFileTypes,
    withCropping = true,
    withPrivacyHint = true,
    small = false,
    fixedAspectRatio,
    ...boxProps
}: Props) {
    const t = useTranslations('file_upload');
    const t_error = useTranslations('error');
    const { colors } = useTheme();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const drop = useRef<HTMLElement | null>(null);
    const breakpoint = useBreakpoint();
    const { user: currentUser } = useUser();

    const [isDraggingOver, setIsDraggingOver] = useToggle();
    const [linkInput, updateLinkInput] = useState('');
    const [isLinkInputValid, setIsLinkInputValid] = useState(true);
    const [fileInCropping, setCroppingFile] = useState<File | undefined>();
    const [fileInfoModalOpenFor, setFileInfoModalOpenFor] = useState<MixedMedia>();
    const [descriptionInputValue, setDescriptionInputValue] = useState('');
    const [sourceInputValue, setSourceInputValue] = useState('');
    const [currentUserIsSource, toggleCurrentUserIsSource] = useToggle();

    const [lastUpdatedFileName, setLastUpdatedFileName] = useState<string>('');
    const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<
        FileUploadError | undefined
    >();

    const maxFileCountReached = maxFileCount && uploads.length >= maxFileCount;

    const allowedMimeTypes = useMemo(() => {
        let mimeTypes = [];
        if (allowedFileTypes?.includes(FileType.Image)) {
            mimeTypes.push('image/*');
        }
        // somehow "application/*"" is not working for pdfs of type "application/pdf", hence being explicit
        if (allowedFileTypes?.includes(FileType.Document)) {
            mimeTypes.push('application/pdf');
            mimeTypes.push('text/*');
        }
        return mimeTypes;
    }, [allowedFileTypes]);

    const handleDrop = (event: DragEvent) => {
        setIsDraggingOver(false);
        event.preventDefault();
        const file = event.dataTransfer?.files[0];

        if (file) addFile(file, uploads);
    };

    const handleDragOver = () => setIsDraggingOver(true);
    const handleLeaveDragOver = () => setIsDraggingOver(false);

    useDragAndDrop(drop.current, handleDrop, handleDragOver, handleLeaveDragOver);

    const userSource = useMemo(
        () => (currentUser ? `${currentUser.firstname} ${currentUser.lastname}` : ''),
        [currentUser]
    );

    /* 
      in rare cases, the file size of a .jpeg increases after cropping
      https://graphicdesign.stackexchange.com/questions/51631/why-does-a-jpegs-file-size-increase-after-cropping-and-saving-for-web-in-photosh 
    
    */
    const onSaveCroppedImage = async (croppedImage: Blob) => {
        if (!fileInCropping) return;

        const isValid = await validateFile(
            fileInCropping,
            currentFilesTotalSize,
            allowedMimeTypes,
            setFileUploadErrorMessage
        );
        if (!isValid) {
            // close cropping modal if not valid
            setCroppingFile(undefined);
            return;
        }

        updateUploads([
            ...uploads,
            {
                // TODO: Do we need this?
                id: Date.now(),
                name: fileInCropping.name,
                data: croppedImage
            }
        ]);

        setCroppingFile(undefined);
    };

    const handleCropError = () => {
        setCroppingFile(undefined);
        setFileUploadErrorMessage(FileUploadError.IMAGE_CROP);
    };

    // Trigger a click event on the hidden file input
    const onUploadClick = () => fileInputRef?.current?.click();

    // we pass uploads as parameter to prevent possible stale closure
    const addFile = async (file: File, uploads: MixedMedia[]) => {
        // ignore when upload is already in list
        const freshlyUploadedFiles = uploads.filter((u) => !isUploadedMedia(u)) as FileToUpload[];
        if (freshlyUploadedFiles.find(({ data }) => data === file)) return;
        setFileUploadErrorMessage(undefined);
        setLastUpdatedFileName(file.name);

        if (isImage(file) && withCropping) {
            setCroppingFile(file);
        } else {
            // for other files like pdfs or docs
            const isValid = await validateFile(
                file,
                currentFilesTotalSize,
                allowedMimeTypes,
                setFileUploadErrorMessage
            );
            if (!isValid) {
                return;
            }

            updateUploads([
                ...uploads,
                {
                    // TODO: Do we need this?
                    id: Date.now(),
                    data: file,
                    name: file.name
                }
            ]);
        }
    };

    const handleFileInput = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];

        if (file) await addFile(file, uploads);
    };

    const onAddLink = () => {
        const isValid = isURL(linkInput);
        setIsLinkInputValid(isValid);
        updateHasError(!isValid);

        if (
            !isValid ||
            uploads.find((u) => (isUploadedMedia(u) ? u.url === linkInput : u.data === linkInput))
        )
            return;

        updateUploads([
            ...uploads,
            {
                // TODO: Do we need this?
                id: Date.now(),
                data: linkInput
            }
        ]);
        updateLinkInput('');
    };

    const removeFile = (id: number) => updateUploads(uploads.filter((f) => f.id !== id));

    const openFileInfoModal = (fileId: number) => {
        const upload = uploads.find((u) => u.id === fileId);
        upload && setFileInfoModalOpenFor(upload);
        upload?.description && setDescriptionInputValue(upload?.description);

        if (upload?.source)
            upload.source === userSource
                ? toggleCurrentUserIsSource(true)
                : setSourceInputValue(upload?.source);
    };

    const getModalTitle = () => {
        if (!fileInfoModalOpenFor) return;
        if (isUploadedMedia(fileInfoModalOpenFor))
            return fileInfoModalOpenFor.name ?? fileInfoModalOpenFor.url;
        else
            return isLink(fileInfoModalOpenFor?.data)
                ? fileInfoModalOpenFor?.data
                : (fileInfoModalOpenFor as FileToUpload).name;
    };

    const resetValues = () => {
        setFileInfoModalOpenFor(undefined);
        setDescriptionInputValue('');
        setSourceInputValue('');
        toggleCurrentUserIsSource(false);
        setLastUpdatedFileName('');
        setFileUploadErrorMessage(undefined);
    };

    const submitFileInfo = () => {
        let newSourceValue: string;

        if (!currentUserIsSource) newSourceValue = sourceInputValue;
        else if (currentUserIsSource && currentUser !== undefined) {
            newSourceValue = userSource;
        } else console.log('Could not update file source. No current user!');

        updateUploads(
            uploads.map((upload) =>
                upload.id === fileInfoModalOpenFor?.id
                    ? {
                          ...upload,
                          description: descriptionInputValue,
                          source: newSourceValue
                      }
                    : upload
            )
        );

        resetValues();
    };

    const renderUploadBox = () =>
        small ? (
            <UploadBoxSmall
                ref={drop}
                onClick={onUploadClick}
                isDraggingOver={isDraggingOver}
                {...boxProps}
            >
                <UploadFile sx={{ color: colors.text }} />
                <input
                    type='file'
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileInput}
                    accept={allowedMimeTypes?.join(',')}
                />

                <TextButton
                    label={title ?? t('upload_file')}
                    labelColor={colors.lightSurfaceTint}
                />
                {infoText && <UploadInfoTextSmall>{infoText}</UploadInfoTextSmall>}
            </UploadBoxSmall>
        ) : (
            <UploadBox
                ref={drop}
                onClick={onUploadClick}
                py={5}
                isDraggingOver={isDraggingOver}
                {...boxProps}
            >
                <input
                    type='file'
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileInput}
                    accept={allowedMimeTypes?.join(',')}
                />
                <UploadFile sx={{ width: 48, height: 48, color: colors.text }} />
                <TextButton
                    label={title ?? t('upload_file')}
                    labelColor={colors.lightSurfaceTint}
                />
                {infoText && <UploadInfoText>{infoText}</UploadInfoText>}
            </UploadBox>
        );
    return (
        <>
            <Center gap={3} style={{ height: 'auto' }}>
                {!maxFileCountReached && withLinkUpload ? (
                    <DHTextField
                        fullWidth
                        name='links'
                        isValid={isLinkInputValid}
                        value={linkInput}
                        placeholder={t('upload_link_input_placeholder')}
                        label={t('upload_link_input_label')}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    icon={<FileUploadIcon color='inherit' />}
                                    onClick={onAddLink}
                                />
                            )
                        }}
                        onKeyDown={onEnter(onAddLink)}
                        onChange={(e) => updateLinkInput(e.target.value)}
                    />
                ) : null}
                {!maxFileCountReached ? renderUploadBox() : null}

                <FileList
                    uploads={uploads}
                    withDescription={!!withDescription}
                    withSource={!!withSource}
                    onRemoveItem={removeFile}
                    onEditFileInfo={openFileInfoModal}
                />
                {withPrivacyHint && <PrivacyHint />}
            </Center>
            <ModalDialog
                open={fileInfoModalOpenFor !== undefined}
                title={getModalTitle() ?? ''}
                onClose={resetValues}
                onSubmit={submitFileInfo}
                submitText={t('save')}
                modalContentStyle={{
                    maxWidth: getBreakpointValue(breakpoint, 248, 348, 489)
                }}
            >
                <Column mt={2} mb={3} gap={2}>
                    {withDescription ? (
                        <DHTextField
                            fullWidth
                            maxLength={100}
                            label={t('add_file_description')}
                            value={descriptionInputValue}
                            onChange={(e) => setDescriptionInputValue(e.target.value)}
                            sx={{
                                width: getBreakpointValue(breakpoint, 200, 300, 441)
                            }}
                        />
                    ) : null}
                    {withSource ? (
                        <SourceInputs breakpoint={breakpoint}>
                            <Row
                                mr={isCompact(breakpoint) ? 0 : 3}
                                mb={isCompact(breakpoint) ? 1 : 0}
                            >
                                <Checkbox
                                    checked={currentUserIsSource}
                                    value={currentUserIsSource}
                                    onChange={() => toggleCurrentUserIsSource()}
                                />
                                <BodyMedium>{t('source_checkbox_label')}</BodyMedium>
                            </Row>
                            <DHTextField
                                sx={{ flex: 1 }}
                                disabled={currentUserIsSource}
                                label={t('source_input_label')}
                                value={currentUserIsSource ? '' : sourceInputValue}
                                onChange={(e) => setSourceInputValue(e.target.value)}
                            />
                        </SourceInputs>
                    ) : null}
                </Column>
            </ModalDialog>
            {withCropping && fileInCropping && (
                <div>
                    <CropModal
                        open={!!fileInCropping}
                        title={fileInCropping.name}
                        imageSrc={URL.createObjectURL(fileInCropping)}
                        mimeType={fileInCropping.type}
                        onClose={() => setCroppingFile(undefined)}
                        onError={handleCropError}
                        onSave={onSaveCroppedImage}
                        aspect={fixedAspectRatio}
                    />
                </div>
            )}
            <PMPSnackbar
                isOpen={!!fileUploadErrorMessage}
                toggleIsOpen={() => setFileUploadErrorMessage(undefined)}
                message={
                    fileUploadErrorMessage
                        ? t(fileUploadErrorMessage, {
                              filename: lastUpdatedFileName
                          })
                        : t_error('title')
                }
                contentPropsSx={{ backgroundColor: colors.error }}
            />
        </>
    );
}
